import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import inputStyles from '@/shared/ui/Input/Input.module.css';

export type DiaMesInputProps = {
  label?: string;
  /** Nome acessível quando não há rótulo visível. */
  ariaLabel?: string;
  /** Texto `dd/MM`, parcial (`15/`) ou vazio. */
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  helperText?: string;
  className?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
};

type Segmento = 'dia' | 'mes';

const LIMITE: Record<Segmento, number> = { dia: 31, mes: 12 };

/** Normaliza colagem ou digitação livre para `dd/MM` (parcial se faltar o mês). */
export function formatarDiaMesInput(texto: string): string {
  const bruto = texto.trim();
  let dia = '';
  let mes = '';
  if (bruto.includes('/')) {
    const [parteDia = '', parteMes = ''] = bruto.split('/');
    dia = parteDia.replace(/\D/g, '').slice(0, 2);
    mes = parteMes.replace(/\D/g, '').slice(0, 2);
  } else {
    const digitos = bruto.replace(/\D/g, '').slice(0, 4);
    dia = digitos.slice(0, 2);
    mes = digitos.slice(2, 4);
  }
  if (!dia && !mes) return '';
  if (dia && mes) return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}`;
  if (dia) return dia.length === 2 ? `${dia.padStart(2, '0')}/` : dia;
  return `/${mes.padStart(2, '0')}`;
}

export function pontaParaDiaMes(ponta: { dia: string; mes: string }): string {
  return formatarDiaMesInput(
    ponta.dia || ponta.mes ? `${ponta.dia}/${ponta.mes}` : '',
  );
}

export function diaMesParaPonta(texto: string): { dia: string; mes: string } {
  const [dia = '', mes = ''] = texto.split('/');
  return {
    dia: dia.replace(/\D/g, '').slice(0, 2),
    mes: mes.replace(/\D/g, '').slice(0, 2),
  };
}

function partes(display: string): { dia: string; mes: string } {
  const [dia = '', mes = ''] = display.split('/');
  return { dia, mes };
}

function montar(dia: string, mes: string): string {
  if (!dia && !mes) return '';
  if (!mes) return `${dia}/`;
  if (!dia) return `/${mes}`;
  return `${dia}/${mes}`;
}

/**
 * Dia e mês sem ano: exibe e devolve `dd/MM`.
 * Digitação por segmentos, no mesmo ritmo do DateInput.
 */
export const DiaMesInput = forwardRef<HTMLInputElement, DiaMesInputProps>(function DiaMesInput(
  {
    label,
    ariaLabel,
    value = '',
    onChange,
    onBlur,
    error,
    helperText,
    className,
    id,
    name,
    disabled,
    required,
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = error ? `${inputId}-error` : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const [segmento, setSegmento] = useState<Segmento>('dia');
  const [digitosDoSegmento, setDigitosDoSegmento] = useState('');

  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

  const emitir = (proximo: string) => {
    onChange?.(proximo);
  };

  const selecionarSegmento = (proximo: Segmento, limparBuffer = true) => {
    setSegmento(proximo);
    if (limparBuffer) setDigitosDoSegmento('');
    requestAnimationFrame(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus();
      const barra = input.value.indexOf('/');
      if (proximo === 'dia') {
        const fim = barra === -1 ? input.value.length : Math.min(2, barra);
        input.setSelectionRange(0, fim);
        return;
      }
      const inicio = barra === -1 ? input.value.length : barra + 1;
      input.setSelectionRange(inicio, input.value.length);
    });
  };

  const atualizarSegmento = (alvo: Segmento, valor: string) => {
    const atual = partes(value);
    emitir(montar(alvo === 'dia' ? valor : atual.dia, alvo === 'mes' ? valor : atual.mes));
  };

  const handleFocus = () => {
    selecionarSegmento('dia');
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    const posicao = event.currentTarget.selectionStart ?? 0;
    selecionarSegmento(posicao <= 2 ? 'dia' : 'mes');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.defaultPrevented) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      selecionarSegmento(event.key === 'ArrowLeft' ? 'dia' : 'mes');
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      setDigitosDoSegmento('');
      const input = inputRef.current;
      const selecaoCompleta = input != null
        && input.value.length > 0
        && input.selectionStart === 0
        && input.selectionEnd === input.value.length;
      if (!value || selecaoCompleta) {
        emitir('');
        selecionarSegmento('dia');
        return;
      }
      const atual = partes(value);
      if (segmento === 'mes') {
        if (atual.mes) {
          emitir(montar(atual.dia, ''));
          selecionarSegmento('mes');
          return;
        }
        emitir('');
        selecionarSegmento('dia');
        return;
      }
      emitir('');
      selecionarSegmento('dia');
      return;
    }
    if (!/^\d$/.test(event.key) || event.ctrlKey || event.metaKey || event.altKey) return;

    event.preventDefault();
    const input = inputRef.current;
    const selecaoCompleta = input != null
      && input.value.length > 0
      && input.selectionStart === 0
      && input.selectionEnd === input.value.length;
    const segmentoAtual: Segmento = selecaoCompleta || !value ? 'dia' : segmento;
    const bufferAtual = selecaoCompleta || !value ? '' : digitosDoSegmento;
    const novoBuffer = `${bufferAtual}${event.key}`.slice(-2);
    const numero = Number(novoBuffer);

    if (novoBuffer.length === 2 && numero >= 1 && numero <= LIMITE[segmentoAtual]) {
      atualizarSegmento(segmentoAtual, novoBuffer);
      setDigitosDoSegmento('');
      selecionarSegmento('mes');
      return;
    }
    setDigitosDoSegmento(event.key);
    atualizarSegmento(segmentoAtual, `0${event.key}`);
    selecionarSegmento(segmentoAtual, false);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    emitir(formatarDiaMesInput(event.clipboardData.getData('text')));
    setDigitosDoSegmento('');
  };

  const commitBlur = () => {
    const normalizado = formatarDiaMesInput(value);
    if (normalizado !== value) emitir(normalizado);
    onBlur?.();
  };

  return (
    <div className={`${inputStyles.field} ${className ?? ''}`.trim()}>
      {label ? (
        <label htmlFor={inputId} className={inputStyles.label}>
          {label}
          {required ? (
            <>
              {' '}
              <span className={inputStyles.required} aria-hidden="true">
                *
              </span>
            </>
          ) : null}
        </label>
      ) : null}
      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        spellCheck={false}
        placeholder="dd/MM"
        maxLength={5}
        disabled={disabled}
        className={`${inputStyles.input} ${error ? inputStyles.inputError : ''}`}
        aria-label={label ? undefined : ariaLabel}
        aria-invalid={Boolean(error)}
        aria-describedby={errorId}
        aria-required={required || undefined}
        value={value}
        onChange={() => {
          /* digitacao controlada via onKeyDown (segmentos) */
        }}
        onFocus={handleFocus}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={commitBlur}
      />
      {error ? (
        <span id={errorId} className={inputStyles.error} role="alert">
          {error}
        </span>
      ) : helperText ? (
        <span className={inputStyles.helper}>{helperText}</span>
      ) : null}
    </div>
  );
});
